import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Tooltip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { makeStyles } from '@material-ui/core/styles';
import { useAllUsers } from 'context/AllUserDataContext';
import MentionEditor, { MENTION_CLASS } from './MentionEditor';
import getStageColor from '../../data/StageColors';

const ENTRY_TYPES = {
    note: {
        icon: NoteAltOutlinedIcon,
        label: 'Note',
        summary: () => 'added a note',
    },
    stage_change: {
        icon: SwapHorizOutlinedIcon,
        label: 'Stage change',
        summary: () => 'changed the stage',
    },
    follow_up: {
        icon: EventAvailableOutlinedIcon,
        label: 'Follow-up',
        summary: (p) => (p.outcome ? `logged a follow-up — ${p.outcome}` : 'logged a follow-up'),
    },
    system: {
        icon: InfoOutlinedIcon,
        label: 'Update',
        summary: () => 'system update',
    },
};

const useStyles = makeStyles(() => ({
    composerDrawer: { padding: '14px 20px', borderBottom: '1px solid #ece7df' },
    composerTab: { marginBottom: 20 },
    timelineDrawer: { padding: '16px 20px', overflowY: 'auto', flex: 1 },
    timelineTab: {},

    dayLabel: {
        fontSize: '0.72rem !important',
        fontWeight: '600 !important',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: '#8a8375',
        margin: '18px 0 10px',
        '&:first-child': { marginTop: 0 },
    },
    timestamp: {
        fontSize: '0.75rem !important',
        fontWeight: '400 !important',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        color: '#8a8375',
        whiteSpace: 'nowrap',
    },

    entry: { display: 'flex', marginBottom: 12, position: 'relative' },

    card: {
        flex: 1,
        minWidth: 0,
        backgroundColor: '#fffdfb',
        borderRadius: 10,
        padding: '12px 14px',
        border: '1px solid #ece7df',
    },
    cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
    summaryLine: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        fontSize: '0.8rem',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        color: '#1a2b3c',
        lineHeight: 1.4,
    },
    typeIcon: { color: '#7a5c3e', marginRight: 6, flexShrink: 0, fontSize: 16 },
    actorName: { fontWeight: 600 },
    noteText: {
        fontSize: '0.875rem',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        color: '#1a2b3c',
        marginTop: 6,
        whiteSpace: 'pre-wrap',
        lineHeight: 1.5,
    },
    detailText: {
        fontSize: '0.875rem',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        color: '#6b6558',
        marginTop: 4,
    },
    criticalFollowUp: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        padding: '10px 12px',
        marginBottom: 12,
        backgroundColor: '#fff8ed',
        border: '1px solid #f0d7b5',
        borderRadius: 8,
    },
    criticalFollowUpIcon: { color: '#b45f06', fontSize: '15px !important', marginTop: 1 },
    criticalFollowUpTitle: {
        fontSize: '14px !important',
        fontWeight: 600,
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        color: '#6b4f34',
        paddingLeft: 2
    },
    criticalFollowUpDetails: {
        fontSize: '15px !important',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        color: '#6b6558',
        marginTop: 3,
    },

    stageRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' },
    stagePill: {
        fontSize: '0.75rem',
        fontWeight: 600,
        padding: '4px 12px',
        borderRadius: 999,
        lineHeight: 1.4,
    },
    stageArrow: { color: '#a69d90', display: 'flex', alignItems: 'center', fontSize: 16 },

    nextFollowUp: {
        display: 'inline-block',
        marginTop: 8,
        fontSize: '0.75rem',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 600,
        color: '#55775b',
        backgroundColor: '#edf3ed',
        borderRadius: 4,
        padding: '3px 8px',
    },
    mentionHighlight: {
        color: '#7a5c3e',
        backgroundColor: '#f2f0ec',
        padding: '2px 4px',
        borderRadius: '4px',
        fontWeight: 500,
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    mentionEditor: {
        width: '100%',
        minHeight: 110,
        padding: '10px 14px',
        boxSizing: 'border-box',
        border: '1px solid #d0c9bf',
        borderRadius: 4,
        fontSize: '14px',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        lineHeight: 1.5,
        color: '#1a2b3c',
        backgroundColor: '#fff',
        outline: 'none',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        cursor: 'text',
        '&:focus': {
            borderColor: '#7a5c3e',
        },
        '&:empty::before': {
            content: 'attr(data-placeholder)',
            color: '#8a8375',
            pointerEvents: 'none',
        },
        [`& .${MENTION_CLASS}`]: {
            color: '#7a5c3e',
            backgroundColor: '#f2f0ec',
            padding: '1px 4px',
            borderRadius: 4,
            fontWeight: 500,
            display: 'inline-block',
        },
    },
}));

const formatAbsolute = (iso) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${d.toLocaleDateString('en-GB')} · ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const formatEntryDate = (iso) => {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const dayBucket = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const startOf = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Helper to parse the saved string "Hello @[Name](id)" into stylized JSX
const formatNoteWithMentions = (text, classes) => {
    if (!text) return null;
    const parts = text.split(/(@\[.+?\]\(.+?\))/g);
    const mentionRegex = /@\[(.+?)\]\((.+?)\)/;

    return parts.map((part, index) => {
        const match = part.match(mentionRegex);
        if (match) {
            const [, name] = match;
            return (
                <span key={index} className={classes.mentionHighlight}>
                    @{name}
                </span>
            );
        }
        return <span key={index}>{part}</span>;
    });
};

// Utils
const CRM_META_PREFIX = '§§CRM_META§§';

// actionType: 'Note' | 'SystemGenerated' | 'StageChange' | 'StageChangeWithNote'
export function encodeNotePayload({ actionType, note = '', fromStage, toStage }) {
    if (actionType === 'Note') {
        // Plain notes stay raw text — supports @mentions as-is, no wrapping overhead
        return note;
    }
    const meta = {
        actionType,
        note,
        ...(fromStage !== undefined && { fromStage }),
        ...(toStage !== undefined && { toStage }),
    };
    return CRM_META_PREFIX + JSON.stringify(meta);
}

export function decodeNotePayload(crmNotes) {
    if (typeof crmNotes === 'string' && crmNotes.startsWith(CRM_META_PREFIX)) {
        try {
            return JSON.parse(crmNotes.slice(CRM_META_PREFIX.length));
        } catch {
            // corrupted/truncated meta — fall through to plain-text handling
        }
    }
    return { actionType: 'Note', note: crmNotes };
}

// Adapts a raw note item into the common shape { type, actorName, timestamp, payload }.
// Real API shape today: { id, crmContactId, userId, crmAccountId, crmNotes, crmNoteDate }
const legacyStageMatch = (text) => text?.match(/^Moved from (.+?) to (.+)$/i);

const parseStageJSON = (raw) => {
    try {
        const obj = JSON.parse(raw);
        return obj && typeof obj === 'object' ? obj : {};
    } catch {
        return {}; // malformed/legacy — degrade gracefully, never throw
    }
};

const normalizeEntry = (item, userNameById) => {
    if (item.type && item.payload) return item;

    const base = {
        id: item.id,
        actorId: item.userId,
        actorName: userNameById.get(String(item.userId)),
        timestamp: item.crmNoteDate,
        __raw: item,
    };

    switch (item.actionType) {
        case 'SystemGenerated':
            return { ...base, type: 'system', payload: { text: item.crmNotes } };

        case 'StageChange': {
            const { fromStage, toStage } = parseStageJSON(item.crmNotes);
            return { ...base, type: 'stage_change', payload: { fromStage, toStage } };
        }

        case 'StageChangeWithNote': {
            const { fromStage, toStage, note } = parseStageJSON(item.crmNotes);
            return { ...base, type: 'stage_change', payload: { fromStage, toStage, note } };
        }

        case 'Note':
            return { ...base, type: 'note', payload: { text: item.crmNotes } };

        default:
            // Legacy rows predating actionType — one-time fallback only
            return { ...base, type: 'note', payload: { text: item.crmNotes } };
    }
};

// Small pill for a single stage name, background/text derived from getStageColor.
function StagePill({ name, classes }) {
    const color = getStageColor(name);
    return (
        <span className={classes.stagePill} style={{ color, backgroundColor: `${color}1f` }}>
            {name}
        </span>
    );
}

export default function NotesPanel({
    notes = [],
    loading = false,
    onAddNote,
    currentUserId,
    variant = 'tab',
    criticalFollowUp = null,
}) {
    const classes = useStyles();
    const editorRef = useRef(null);
    const [hasContent, setHasContent] = useState(false);

    const { allUsers = [] } = useAllUsers();

    const mentionableUsers = allUsers.map((user) => ({
        id: String(user.userId),
        display: user.defaultDriverDisplayName,
        email: user.defaultDriverUserName,
    }));

    const userNameById = useMemo(() => {
        const map = new Map();
        allUsers.forEach((u) => map.set(String(u.userId), u.defaultDriverDisplayName));
        return map;
    }, [allUsers]);

    const getMentionedUserIds = (text) => {
        const regex = /@\[(.+?)\]\((.+?)\)/g;
        return [...text.matchAll(regex)].map((match) => match[2]);
    };

    const handleSend = async () => {
        const value = editorRef.current?.getValue() || '';
        if (!value.trim()) return;

        const mentionedUserIds = getMentionedUserIds(value);
        const mentionedUsers = allUsers.filter((user) => mentionedUserIds.includes(String(user.userId)));

        await onAddNote(value, mentionedUsers);

        editorRef.current.clear();
        setHasContent(false);
    };

    const entries = useMemo(() => {
        return notes
            .map((n) => normalizeEntry(n, userNameById))
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }, [notes, userNameById]);

    let lastBucket = null;

    return (
        <>
            <Box className={variant === 'drawer' ? classes.composerDrawer : classes.composerTab}>
                <MentionEditor
                    ref={editorRef}
                    users={mentionableUsers}
                    placeholder="Add a note about this contact... (Type @ to mention)"
                    className={classes.mentionEditor}
                    onDirtyChange={setHasContent}
                />

                <Box display="flex" justifyContent="flex-end" marginTop="8px">
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        endIcon={<SendIcon fontSize="small" />}
                        disabled={!hasContent}
                        onClick={handleSend}
                        style={{ textTransform: 'none' }}
                        sx={{
                            backgroundColor: '#7a5c3e',
                            '&:hover': { backgroundColor: '#6b4f34' },
                        }}
                    >
                        Add Note
                    </Button>
                </Box>
            </Box>

            <Box className={variant === 'drawer' ? classes.timelineDrawer : classes.timelineTab}>
                {criticalFollowUp && (
                    <Box className={classes.criticalFollowUp} role="status">
                        {criticalFollowUp.status === 'overdue' ? (
                            <ErrorOutlineIcon className={classes.criticalFollowUpIcon} />
                        ) : (
                            <WarningAmberIcon className={classes.criticalFollowUpIcon} />
                        )}
                        <Box>
                            <Typography className={classes.criticalFollowUpTitle}>
                                System generated · Follow-up {criticalFollowUp.status === 'overdue' ? 'overdue' : 'due soon'}
                            </Typography>
                            <Typography className={classes.criticalFollowUpDetails}>
                                {[
                                    criticalFollowUp.date && `Follow up ${criticalFollowUp.date}`,
                                    criticalFollowUp.stage && `Stage: ${criticalFollowUp.stage}`,
                                ].filter(Boolean).join(' · ')}
                            </Typography>
                        </Box>
                    </Box>
                )}
                {loading ? (
                    <Box display="flex" justifyContent="center" padding="24px">
                        <CircularProgress size={26} />
                    </Box>
                ) : entries.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" align="center" style={{ marginTop: 24 }}>
                        No activity yet for this contact.
                    </Typography>
                ) : (
                    entries.map((entry, idx) => {
                        const bucket = dayBucket(entry.timestamp);
                        const showDayLabel = bucket !== lastBucket;
                        lastBucket = bucket;

                        const config = ENTRY_TYPES[entry.type] ?? ENTRY_TYPES.note;
                        const TypeIcon = config.icon;

                        return (
                            <React.Fragment key={entry.id ?? idx}>
                                {showDayLabel && <Typography className={classes.dayLabel}>{bucket}</Typography>}
                                <Box className={classes.entry}>
                                    <Box className={classes.card}>
                                        <Box className={classes.cardHeader}>
                                            <Typography className={classes.summaryLine}>
                                                <TypeIcon className={classes.typeIcon} />
                                                <span>
                                                    {config.label} · <span className={classes.actorName}>{entry.actorName || 'Someone'}</span>{' '}
                                                    {config.summary(entry.payload)}
                                                </span>
                                            </Typography>
                                            <Tooltip title={formatAbsolute(entry.timestamp)}>
                                                <Typography className={classes.timestamp}>{formatEntryDate(entry.timestamp)}</Typography>
                                            </Tooltip>
                                        </Box>

                                        {entry.type === 'note' && (
                                            <Typography className={classes.noteText}>
                                                {formatNoteWithMentions(entry.payload.text, classes)}
                                            </Typography>
                                        )}

                                        {entry.type === 'stage_change' && (
                                            <Box className={classes.stageRow}>
                                                <StagePill name={entry.payload.fromStage} classes={classes} />
                                                <ArrowForwardIcon className={classes.stageArrow} fontSize="small" />
                                                <StagePill name={entry.payload.toStage} classes={classes} />
                                            </Box>
                                        )}

                                        {entry.type === 'stage_change' && entry.payload.note && (
                                            <Typography className={classes.noteText}>
                                                {formatNoteWithMentions(entry.payload.note, classes)}
                                            </Typography>
                                        )}

                                        {entry.type === 'system' && entry.payload.text && (
                                            <Typography className={classes.detailText}>{entry.payload.text}</Typography>
                                        )}

                                        {entry.type === 'follow_up' && entry.payload.notes && (
                                            <Typography className={classes.detailText}>{entry.payload.notes}</Typography>
                                        )}

                                        {entry.type === 'follow_up' && entry.payload.nextFollowUpDate && (
                                            <Box className={classes.nextFollowUp}>
                                                Next follow-up · {formatAbsolute(entry.payload.nextFollowUpDate).split(' · ')[0]}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </React.Fragment>
                        );
                    })
                )}
            </Box>
        </>
    );
}